import angular, { IWindowService, isNumber } from "angular";
import {
  vsName as csvWriterServiceName,
  CsvWriter,
  CsvColumnConfig,
} from "../../../records/csv/csvWriter";
import {
  booleanFormatterFactory,
  currencyFormatter,
} from "../../../records/csv/formatters";
import { IApiResponse, IQuery } from "../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";

interface ReconciliationReportResponse {
  Readings: any[];
  Liability: number;
  RequiredReadingCount: number;
  ReadingCount: number;
  ReportFields: string[];
}

export class DailyReadingsViewController {
  static $inject: string[] = [
    "date",
    "$scope",
    "$q",
    csvWriterServiceName,
    "$http",
    ServerUtilityName,
    "$window",
  ];

  datePickerOpened: boolean = false;
  date: Date;

  private readonly aggregate: IProgressiveAggregate = {
    completeMachineCount: 0,
    completeReadingCount: 0,
    liability: 0,
    machineCount: 0,
    readingCount: 0,
    liabilityChange: 0,
  };
  constructor(
    date: Date,
    $scope: angular.IScope,
    private readonly $q: angular.IQService,
    private readonly csvWriter: CsvWriter,
    private readonly $http: angular.IHttpService,
    private readonly serverUtility: ServerUtility,
    private readonly $window: IWindowService
  ) {
    this.date = date;

    $scope.$watch(
      () => this.date,
      (newDate: Date, oldDate: Date) =>
        this.dateChanged($scope, newDate, oldDate)
    );
  }

  dateChanged($scope: angular.IScope, newDate: Date, oldDate: Date): void {
    const jackpotAggregatePromise = this.updateJackpotAggregateInfo(newDate);
    const machineAggregatePromise = this.updateMachineAggregateInfo(newDate);

    if (newDate?.getTime() === oldDate?.getTime()) {
      return;
    }

    this.$q.all([jackpotAggregatePromise, machineAggregatePromise]).then(() => {
      $scope.$broadcast("dateChanged", newDate);
    });
  }

  getReconciliationReport(): void {
    this.$http<Blob>({
      method: "get",
      url: "/api/machines/progressives/ProgressiveDailyPeriod/actions/GetReconciliationReport.csv",
      params: {
        Date: this.date,
      },
      responseType: "blob",
    }).then((result) => {
      const blob = <Blob>(<any>result);
      const blobUrl = this.$window.URL.createObjectURL(blob, {
        type: "text/csv",
      });
      const downloadLink = angular.element("<a></a>");
      downloadLink.attr("href", blobUrl);
      downloadLink.attr("download", "Reconciliation Report.csv");
      downloadLink[0].click();
      downloadLink.remove();
    });
  }

  getPercentage(fraction: number, whole: number) {
    return Math.floor((fraction / whole) * 100.0);
  }

  private updateJackpotAggregateInfo(date: Date): angular.IPromise<void> {
    const completedCountTerm =
      "count(progressivedailyreadinggamelevelreadingidofrecord)";
    const totalCountTerm = "count(*)";
    const sumLiabilityTerm = "sum(liability)";
    const sumChangeTerm = "sum(change)";

    const query: IQuery = {
      ProgressiveDailyPeriodDate: date,
      Take: 0,
      Include: `${totalCountTerm},${completedCountTerm},${sumLiabilityTerm},${sumChangeTerm}`,
    };

    return this.serverUtility
      .query({
        url: "/api/machines/progressives/ProgressiveDailyReading/GameLevel",
        query,
      })
      .then((queryResponse) => {
        const meta = queryResponse.Meta;
        this.aggregate.completeReadingCount =
          Number.parseInt(meta[completedCountTerm]) || 0;
        this.aggregate.readingCount =
          Number.parseInt(meta[totalCountTerm]) || 0;
        this.aggregate.liability =
          Number.parseFloat(meta[sumLiabilityTerm]) || 0;
        this.aggregate.liabilityChange =
          Number.parseFloat(meta[sumChangeTerm]) || 0;
      });
  }

  private updateMachineAggregateInfo(
    date: Date
  ): angular.IPromise<[void, void]> {
    const query: IQuery = {
      ProgressiveDailyPeriodDate: date,
      Take: 0,
      Include: "count(*)",
    };

    const url = "/api/machines/progressives/ProgressiveDailyMachine";
    const totalCountPromise = this.serverUtility
      .query({ url, query })
      .then((queryResponse) => {
        const meta = queryResponse.Meta;
        this.aggregate.machineCount = Number.parseInt(meta["count(*)"]) || 0;
      });

    const doneCountPromise = this.serverUtility
      .query({
        url,
        query: {
          ...query,
          Complete: true,
        },
      })
      .then((queryResponse) => {
        this.aggregate.completeMachineCount = queryResponse.Total;
      });

    return this.$q.all([totalCountPromise, doneCountPromise]);
  }

  showDatepicker($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.datePickerOpened = true;
  }
}

interface IProgressiveAggregate {
  readingCount: number;
  machineCount: number;
  completeReadingCount: number;
  completeMachineCount: number;
  liability: number;
  liabilityChange: number;
}
